import { CdkListboxModule } from '@angular/cdk/listbox';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  signal,
  input,
  WritableSignal,
} from '@angular/core';
import { type AfterViewInit, ChangeDetectorRef, DestroyRef, type OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { PalantirAccordionComponent } from '../../../../accordion/accordion.component';
import { PalantirCheckboxComponent } from '../../../../checkbox/checkbox.component';
import { PalantirIconComponent } from '../../../../icon/icon.component';
import { PalantirInputComponent } from '../../../../input/input.component';
import { PalantirRadioComponent } from '../../../../radio/radio.component';
import { PalantirSkeletonComponent } from '../../../../skeleton/skeleton.component';
import { PalantirTabsModule } from '../../../../tabs/tabs.module';
import { PalantirTextComponent } from '../../../../text/text.component';
import type { PalantirFilterModalInstance } from '../../../filter-modal.instance';
import {
  PalantirFilterInputType,
  PalantirFilterSearchInputDependsOnBehavior,
  PalantirFilterSearchInputOptionLoaderFailedBehavior,
} from '../../../filter-modal.type';
import type {
  PalantirFilterInputFormType,
  PalantirFilterOption,
  PalantirFilterSearchInput,
  PalantirFilterMultiSearchInput,
  PalantirFilterSelectableValue,
} from '../../../filter-modal.type';
import { PalantirFilterModalErrorDisplayComponent } from '../../shared/error-display/error-display.component';
import { PalantirFilterModalOptionComponent } from '../../shared/option/option.component';
import { filterModalInputLogger } from '../logger.instance';

const logger = filterModalInputLogger.extend('search');

@Component({
  selector: 'palantir-filter-modal-search-input-component',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: true,
  imports: [
    PalantirAccordionComponent,
    PalantirInputComponent,
    ReactiveFormsModule,
    CdkListboxModule,
    PalantirRadioComponent,
    PalantirSkeletonComponent,
    PalantirCheckboxComponent,
    PalantirIconComponent,
    PalantirTextComponent,
    PalantirTabsModule,
    PalantirFilterModalErrorDisplayComponent,
    PalantirFilterModalOptionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PalantirFilterModalSearchInputComponent<
  T extends PalantirFilterInputType.MULTI_SEARCH | PalantirFilterInputType.SEARCH,
>
  implements OnInit, AfterViewInit
{
  public readonly definition =
    input.required<
      T extends PalantirFilterInputType.SEARCH
        ? PalantirFilterSearchInput
        : PalantirFilterMultiSearchInput
    >();

  public readonly control =
    input.required<
      T extends PalantirFilterInputType.SEARCH
        ? PalantirFilterInputFormType[PalantirFilterInputType.SEARCH]
        : PalantirFilterInputFormType[PalantirFilterInputType.MULTI_SEARCH]
    >();

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  public readonly modalInstance = input.required<PalantirFilterModalInstance<{}>>();

  private selectedItems: PalantirFilterOption[] = [];

  public searchControl = new FormControl();

  public isLoading = signal(false);

  public items = signal<PalantirFilterOption[]>([]);

  // eslint-disable-next-line unicorn/consistent-function-scoping
  public canShowFilter = computed(() => {
    const canShow = this.modalInstance().dependsOnFilterComputed(this.definition().dependsOn);
    logger.debug(`Visibility check for "${this.definition().label}":`, canShow);
    return canShow;
  });

  public constructor(
    private readonly destroyRef: DestroyRef,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.initSearchControlListener();
  }

  public async ngAfterViewInit(): Promise<void> {
    this.definition().selectedOptionsLoaderFailedBehavior ??=
      PalantirFilterSearchInputOptionLoaderFailedBehavior.REPLACE;

    await this.loadPreSelectedItems();

    this.modalInstance().reset$.subscribe(() => {
      void this.resetFilter();
    });

    this.initDependencyListener();
  }

  private initSearchControlListener(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.definition().debounceTime ?? 300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(async (value) => {
        logger.debug(`Search query changed for "${this.definition().label}":`, value);
        if (!value) {
          this.items.set(this.selectedItems);
          return;
        }

        await this.loadItems(value);
      });

    if (this.definition().preLoadOptions && this.isControlEmpty()) {
      void this.loadItems('');
      this.searchControl.markAsDirty();
    }
  }

  private isControlEmpty() {
    if (
      this.isMultiSearch() &&
      (this.control() as FormControl<PalantirFilterSelectableValue[]>).value?.length === 0
    ) {
      return true;
    }

    const control = this.control();
    return !control.value || control.value === '' || control.value === null;
  }

  private initDependencyListener(): void {
    const definition = this.definition();
    if (
      !definition.dependsOn ||
      definition.dependsOn.dependencyUpdateBehavior ===
        PalantirFilterSearchInputDependsOnBehavior.IGNORE
    ) {
      return;
    }

    const updateBehavior = definition.dependsOn.dependencyUpdateBehavior;

    this.modalInstance()
      .formGroup.get(definition.dependsOn.filter)
      ?.valueChanges.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(async () => {
        if (updateBehavior === PalantirFilterSearchInputDependsOnBehavior.RESET_IF_CHANGED) {
          void this.resetFilter();
          return;
        }

        await this.loadItems(this.searchControl.value).catch(() => {});
        this.unselectItemIfNotInOptions();
      });
  }

  private async loadPreSelectedItems(): Promise<void> {
    if (this.isControlEmpty()) {
      return;
    }

    this.isLoading.set(true);

    try {
      this.selectedItems = await this.fetchPreSelectedValue();
    } catch (error) {
      logger.error(
        `Failed to load pre-selected items for filter "${this.definition().label}":`,
        error,
      );
      switch (this.definition().selectedOptionsLoaderFailedBehavior!) {
        case PalantirFilterSearchInputOptionLoaderFailedBehavior.REPLACE:
          this.setControlToEmpty();
          this.selectedItems = [];
          break;
        case PalantirFilterSearchInputOptionLoaderFailedBehavior.KEEP:
          this.selectedItems = this.getDefaultPreSelectedValue();
          break;

        case PalantirFilterSearchInputOptionLoaderFailedBehavior.RESET:
          this.setControlToEmpty();
          void this.resetFilter();
          break;
      }
    } finally {
      this.isLoading.set(false);
    }

    this.searchControl.markAsDirty();
    this.updateSelectedItems();
  }

  private async resetFilter(): Promise<void> {
    await this.loadPreSelectedItems();
    this.searchControl.reset();
    this.updateSelectedItems(true);
  }

  private async loadItems(value: string): Promise<void> {
    const definition = this.definition();
    if (!definition.searchFn) {
      throw new Error('Search function is not defined');
    }

    this.isLoading.set(true);
    try {
      const items = await definition.searchFn(value, this.modalInstance());
      logger.debug(`Search results for "${definition.label}" (query: "${value}"):`, items.length);
      this.items.set(items);
    } catch (error) {
      logger.error(
        `Search function failed for filter "${definition.label}" with query "${value}":`,
        error,
      );
      this.items.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  public selectItem(item: PalantirFilterOption): void {
    if (this.isMultiSearch() && !this.isSingleSearch()) {
      this.selectItems(item);

      return;
    }

    if (!this.isSingleSearch()) {
      return;
    }

    const control = this.control() as FormControl<PalantirFilterSelectableValue>;

    if (control?.value === item.value) {
      (control as FormControl<PalantirFilterSelectableValue>).setValue('');
      this.selectedItems = [];
      control.markAsDirty();
      this.updateSelectedItems();

      return;
    }

    control.setValue(item.value);
    this.selectedItems = [item];
    logger.debug(`Selected single item for "${this.definition().label}":`, item.value);
    control.markAsDirty();
    this.updateSelectedItems();
  }

  private selectItems(item: PalantirFilterOption): void {
    if (!this.isMultiSearch()) {
      return;
    }

    const control = this.control() as FormControl<PalantirFilterSelectableValue[]>;
    const controlValue = control.value ?? [];

    if (controlValue?.includes(item.value)) {
      control.setValue(
        controlValue.filter((value: PalantirFilterSelectableValue) => value !== item.value),
      );
      this.selectedItems = this.selectedItems.filter(
        (selectedItem) => selectedItem.value !== item.value,
      );
    }

    if (!controlValue?.includes(item.value)) {
      control.setValue([...controlValue, item.value]);

      if (this.selectedItems.some((selectedItem) => selectedItem.value === item.value)) {
        return;
      }

      this.selectedItems.push(item);
    }

    control.markAsDirty();
    this.updateSelectedItems();
  }

  private getDefaultPreSelectedValue(): PalantirFilterOption[] {
    if (this.isMultiSearch()) {
      const control = this.control() as FormControl<PalantirFilterSelectableValue[]>;
      return control.value.map<PalantirFilterOption>((value) => ({
        label: value as string,
        value,
      }));
    }

    if (this.isSingleSearch()) {
      return this.control().value
        ? [
            {
              label: this.control().value as string,
              value: this.control().value as string,
            },
          ]
        : [];
    }

    return [];
  }

  private async fetchPreSelectedValue(): Promise<PalantirFilterOption[]> {
    if (this.isMultiSearch()) {
      return this.fetchMultiPreSelectedValue();
    }

    if (this.isSingleSearch()) {
      return this.fetchSinglePreSelectedValue();
    }

    return [];
  }

  private async fetchMultiPreSelectedValue(): Promise<PalantirFilterOption[]> {
    if (!this.isMultiSearch()) {
      return [];
    }

    const control = this.control() as FormControl<PalantirFilterSelectableValue[]>;

    const result = (await this.definition().selectedOptionsLoaderFn!(
      control.value as PalantirFilterSelectableValue & PalantirFilterSelectableValue[],
    )) as PalantirFilterOption[];

    const missingValues = control.value.filter(
      (value) => !result.some((item) => item.value === value),
    );

    const missingOrInvalidValues = missingValues.length || control.value.length !== result.length;

    if (!missingOrInvalidValues) {
      return result;
    }

    const definition = this.definition();

    logger.error(`Failed to load pre-selected items for filter "${this.definition().label}":`, {
      missingValues,
      failedBehavior: definition.selectedOptionsLoaderFailedBehavior,
    });

    if (
      definition.selectedOptionsLoaderFailedBehavior ===
      PalantirFilterSearchInputOptionLoaderFailedBehavior.REPLACE
    ) {
      control.setValue(result.map((item) => item.value));
      return result;
    }

    if (
      definition.selectedOptionsLoaderFailedBehavior ===
      PalantirFilterSearchInputOptionLoaderFailedBehavior.RESET
    ) {
      throw new Error('Invalid values');
    }

    return [
      ...result,
      ...missingValues.map<PalantirFilterOption>((value) => ({ label: String(value), value })),
    ];
  }

  private async fetchSinglePreSelectedValue(): Promise<PalantirFilterOption[]> {
    if (!this.isSingleSearch()) {
      return [];
    }

    const control = this.control() as FormControl<PalantirFilterSelectableValue>;

    const loadedItem = (await this.definition().selectedOptionsLoaderFn!(
      control.value as PalantirFilterSelectableValue & PalantirFilterSelectableValue[],
    )) as PalantirFilterOption;

    if (loadedItem?.value === control.value) {
      return [loadedItem];
    }

    const definition = this.definition();
    if (
      definition.selectedOptionsLoaderFailedBehavior ===
      PalantirFilterSearchInputOptionLoaderFailedBehavior.RESET
    ) {
      throw new Error('Invalid value');
    }

    if (
      definition.selectedOptionsLoaderFailedBehavior ===
      PalantirFilterSearchInputOptionLoaderFailedBehavior.REPLACE
    ) {
      control.setValue(loadedItem?.value);
      return [loadedItem];
    }

    return this.getDefaultPreSelectedValue();
  }

  private updateSelectedItems(force = false): void {
    if (this.searchControl.value && !force) {
      return;
    }

    if (!this.items().length && this.selectedItems.length) {
      this.items.set(this.selectedItems);
    }

    this.changeDetectorRef.markForCheck();
  }

  private unselectItemIfNotInOptions(): void {
    const itemsInOptions = this.items().some((item) => {
      if (this.isSingleSearch()) {
        return item.value === this.control().value;
      }

      if (this.isMultiSearch()) {
        const control = this.control() as FormControl<PalantirFilterSelectableValue[]>;
        return control.value.includes(item.value);
      }

      return false;
    });

    if (!itemsInOptions) {
      this.control().reset();
      this.control().markAsDirty();
    }

    this.updateSelectedItems();
  }

  public isMultiSearch(): this is PalantirFilterModalSearchInputComponent<PalantirFilterInputType.MULTI_SEARCH> & {
    control: WritableSignal<FormControl<PalantirFilterSelectableValue[]>>;
  } {
    return this.definition().type === PalantirFilterInputType.MULTI_SEARCH;
  }

  public isSingleSearch(): this is PalantirFilterModalSearchInputComponent<PalantirFilterInputType.SEARCH> & {
    control: WritableSignal<FormControl<PalantirFilterSelectableValue>>;
  } {
    return this.definition().type === PalantirFilterInputType.SEARCH;
  }

  private setControlToEmpty() {
    if (this.isSingleSearch()) {
      (this.control() as FormControl<PalantirFilterSelectableValue>).setValue('');
    }

    if (this.isMultiSearch()) {
      (this.control() as FormControl<PalantirFilterSelectableValue[]>).setValue([]);
    }
  }

  public isChecked(item: PalantirFilterOption): boolean {
    if (this.isMultiSearch()) {
      const control = this.control() as FormControl<PalantirFilterSelectableValue[]>;
      return control.value?.includes(item.value);
    }

    if (this.isSingleSearch()) {
      const control = this.control() as FormControl<PalantirFilterSelectableValue>;
      return control.value === item.value;
    }

    return false;
  }
}
